import type { Editor } from '@tiptap/core';
import { useCallback, useEffect } from 'react';
import type { MutableRefObject } from 'react';

import type { TiptapCellEditingSession } from '../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import type { MNHotInstance } from '../tableHotInstanceTypes';

/**
 * Keeps Handsontable's shortcut context aligned with the focused Tiptap cell editor.
 *
 * Toolbar interactions can return focus to Tiptap while Handsontable is still in its grid context.
 */
export const useTiptapCellEditorShortcutContext = ({
    cellEditingSession,
    editor,
    hotTableInstanceRef,
}: {
    cellEditingSession: TiptapCellEditingSession | null;
    editor: Editor | null;
    hotTableInstanceRef: MutableRefObject<MNHotInstance<TiptapContent, TiptapContent> | null>;
}) => {
    const activateEditorShortcutContext = useCallback(() => {
        hotTableInstanceRef.current?.getShortcutManager().setActiveContextName('editor');
    }, [hotTableInstanceRef]);

    useEffect(() => {
        if (!cellEditingSession || !editor) return;

        editor.on('focus', activateEditorShortcutContext);

        return () => {
            editor.off('focus', activateEditorShortcutContext);
        };
    }, [activateEditorShortcutContext, cellEditingSession, editor]);

    return activateEditorShortcutContext;
};
